import { z, ZodSchema, ZodRawShape } from 'zod';
import { useTranslation } from 'react-i18next';

import { UserDataFields } from '@/pages/Registration/RegistrationUserData.tsx';
import { UserPhotosFields } from '@/pages/Registration/RegistrationUserPhotos.tsx';

const MIN_PHOTOS: number = 1;
const MAX_PHOTOS: number = 3;
const ACCEPTED_IMAGE_MIME_TYPES: string[] = [
  'image/jpeg',
  'image/png',
  'image/webp'
];

const MIN_AGE: number = 18;

export function getUserDataValidationSchema(
  data: UserDataFields
): ZodSchema | null {
  const shape: ZodRawShape = {};
  const { t } = useTranslation();

  shape.name = z
    .any()
    .refine(
      () => data.name?.length >= 2 && data.name?.length <= 20,
      t('validation.name')
    );

  shape.gender = z
    .any()
    .refine(() => data.gender?.length === 1, t('validation.gender'));

  shape.birthday = z
    .string()
    .refine(() => {
      const birthday = new Date(data.birthday);
      return !isNaN(birthday.getTime());
    }, t('validation.birthday'))
    .refine(() => {
      const birthday = new Date(data.birthday);
      const today = new Date();
      const age = today.getFullYear() - birthday.getFullYear();
      return (
        age > MIN_AGE ||
        (age === MIN_AGE &&
          today >=
            new Date(
              today.getFullYear(),
              birthday.getMonth(),
              birthday.getDate()
            ))
      );
    }, t('validation.age'));

  if (Object.keys(shape).length === 0) {
    return null;
  }

  return z.object(shape);
}

export function getUserPhotosValidationSchema(
  data: UserPhotosFields
): ZodSchema | null {
  const shape: ZodRawShape = {};
  const { t } = useTranslation();

  shape.photos = z
    .any()
    .refine(
      () => data.photos?.length >= MIN_PHOTOS,
      t('validation.min_photos', { minPhotos: MIN_PHOTOS })
    )
    .refine(
      () => data.photos?.length <= MAX_PHOTOS,
      t('validation.max_photos', { maxPhotos: MAX_PHOTOS })
    )
    .refine(
      () =>
        data.photos.every((photo) =>
          photo.file
            ? ACCEPTED_IMAGE_MIME_TYPES.includes(photo.file.type)
            : true
        ),
      t('validation.photos_formats')
    );

  if (Object.keys(shape).length === 0) {
    return null;
  }

  return z.object(shape);
}
